import React, { useCallback } from 'react';
import CypAdhdReviewDocument from './CypAdhdReviewDocument';

/**
 * RD_DD_CypAdhd — CYP ADHD Diagnostic Decision review page (ASMT-21).
 *
 * Thin wrapper over the shared CypAdhdReviewDocument. The Decisions for
 * Other Conditions section is DYNAMIC: the backend writes one
 * cyp_dd_secondaryCondition_{N}_sentence per emerging differential, with
 * the condition's name stored under cyp_dd_secondaryCondition_{N}
 * (llm_processor_cyp_dd.py), so the structure is derived from the
 * reportContent doc's outputs. Blanked orphaned keys ("" values) are
 * filtered out here and by the shared component's content filter.
 *
 * The 'ddReportApproved' flag this page's exit writes is what unlocks the
 * PAD stage together with DD completion (cyp_adhd_stage_progress.py).
 */

const DD_INPUT_PANEL_GROUPS = [
  ['Your decisions', 'Clinician Decisions'],
  ['Your notes', 'Clinician Notes'],
];

export function buildDdDocStructure(reportDoc) {
  const outputs = (reportDoc && reportDoc.outputs) || {};

  const structure = {
    'Diagnostic Decision': {
      1: { title: 'Decision for ADHD', code: 'cyp_dd_primaryDiagnosisSentence' },
    },
  };

  // Collect the secondary-condition sentence keys in numeric order and label
  // each with its stored condition name.
  const secondary = {};
  Object.keys(outputs)
    .map((key) => {
      const match = key.match(/^cyp_dd_secondaryCondition_(\d+)_sentence$/);
      return match ? Number(match[1]) : null;
    })
    .filter((n) => n !== null)
    .sort((a, b) => a - b)
    .forEach((n) => {
      const sentence = outputs[`cyp_dd_secondaryCondition_${n}_sentence`];
      if (!sentence) return; // blanked orphan from a shrunk condition set
      secondary[n] = {
        title: outputs[`cyp_dd_secondaryCondition_${n}`] || `Condition ${n}`,
        code: `cyp_dd_secondaryCondition_${n}_sentence`,
      };
    });
  if (Object.keys(secondary).length > 0) {
    structure['Decisions for Other Conditions'] = secondary;
  }

  structure['Final Formulation'] = {
    1: { title: 'Formulation', code: 'cyp_dd_finalFormulation' },
  };

  return structure;
}

export default function RD_DD_CypAdhd() {
  const buildDocStructure = useCallback(buildDdDocStructure, []);
  return (
    <CypAdhdReviewDocument
      stageKey="diagnosticDecision"
      flagPrefix="dd"
      moduleTitle="Diagnostic Decision"
      editStageCode="DD"
      buildDocStructure={buildDocStructure}
      inputPanelGroups={DD_INPUT_PANEL_GROUPS}
    />
  );
}
